<template>
  <div class="profile-section">
    <div class="profile-header">
      <div class="avatar-wrapper">
        <img :src="previewAvatar" alt="Profil Fotoğrafı" class="avatar" />
        <label class="edit-icon" title="Düzenle">
          <input type="file" accept="image/*" @change="handleAvatarChange" hidden />
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="white" viewBox="0 0 24 24">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.18-.18.29-.43.29-.71 0-.28-.11-.53-.29-.71l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.82z"/>
          </svg>
        </label>
      </div>

      <div class="info-group">
        <label>Ad:</label>
        <input v-model="form.name" type="text" />
      </div>
      <div class="info-group">
        <label>Soyad:</label>
        <input v-model="form.surname" type="text" />
      </div>
    </div>

    <div class="profile-body">
      <div class="info-group">
        <label>Doğum Tarihi:</label>
        <input v-model="form.birth_date" type="date" />
      </div>
      <div class="info-group">
        <label>Telefon:</label>
        <input v-model="form.phone" type="tel" />
      </div>

      <div class="info-group">
        <label>Ülke:</label>
        <select v-model="form.country">
          <option disabled value="">Ulke Seç</option>
          <option v-for="c in countries" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div class="info-group" v-if="cities.length">
        <label>İl:</label>
        <select v-model="form.city">
          <option disabled value="">İl Seç</option>
          <option v-for="c in cities" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div class="info-group" v-if="districts.length">
        <label>İlçe:</label>
        <select v-model="form.district">
          <option disabled value="">İlçe Seç</option>
          <option v-for="d in districts" :key="d" :value="d">{{ d }}</option>
        </select>
      </div>

      <div class="info-group">
        <label>Açık Adres:</label>
        <textarea v-model="form.address" rows="2" />
      </div>

      <div v-if="user?.role === 'translator'" class="info-group">
        <label>Uzmanlık Dilleri:</label>
        <input v-model="form.languages" placeholder="Örn: İngilizce, Almanca" />
        <button class="btn secondary" @click="saveLanguages">Dilleri Kaydet</button>
      </div>

      <div v-if="user?.role === 'translator'" class="info-group">
        <label>Sertifika:</label>
        <input type="file" @change="handleCertificate" />
        <p v-if="form.certificateName">Yüklü: {{ form.certificateName }}</p>
        <button class="btn secondary" @click="uploadCertificate" :disabled="!form.certificateFile">Yükle</button>
      </div>
    </div>

    <button class="btn primary" @click="saveProfile">Profil Bilgilerini Kaydet</button>

    <div v-if="toastVisible" :class="['toast', toastType]">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { getToken, getUser } from '@/utils/auth'
import { locationData } from '@/data/locationData'

const user = getUser()
const previewAvatar = ref('')
const toastMessage = ref('')
const toastVisible = ref(false)
const toastType = ref('success')

const form = ref({
  name: '',
  surname: '',
  birth_date: '',
  phone: '',
  country: '',
  city: '',
  district: '',
  address: '',
  languages: '',
  certificateName: '',
  certificateFile: null,
  avatarFile: null
})

const countries = Object.keys(locationData)
const cities = computed(() => locationData[form.value.country] ? Object.keys(locationData[form.value.country]) : [])
const districts = computed(() => locationData[form.value.country]?.[form.value.city] || [])
const baseURL = process.env.VUE_APP_API_URL
function getImageUrl(path) {
  if (!path) return ''
  return path.startsWith('/uploads')
    ? `${baseURL}${path}`
    : path
}
onMounted(fetchProfile)

async function fetchProfile() {
  const res = await fetch(`${baseURL}/settings/profile`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })
  if (res.ok) {
    const data = await res.json()
    previewAvatar.value = getImageUrl(data.avatar_url)
    form.value = {
      ...form.value,
      name: data.name,
      surname: data.surname,
      birth_date: data.birth_date ? data.birth_date.slice(0, 10) : '',
      phone: data.phone,
      country: data.country || '',
      city: data.city || '',
      district: data.district || '',
      address: data.address,
      languages: data.languages || ''
    }
  }
}

function showToast(message, type = 'success') {
  toastMessage.value = message
  toastType.value = type
  toastVisible.value = true
  setTimeout(() => { toastVisible.value = false }, 3000)
}

async function saveProfile() {
  const payload = {
    name: form.value.name,
    surname: form.value.surname,
    birth_date: form.value.birth_date,
    phone: form.value.phone,
    country: form.value.country,
    city: form.value.city,
    district: form.value.district,
    address: form.value.address
  }
  const res = await fetch(`${baseURL}/settings/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`
    },
    body: JSON.stringify(payload)
  })
  showToast(res.ok ? 'Profil güncellendi!' : 'Hata oluştu.', res.ok ? 'success' : 'error')
}

async function saveLanguages() {
  const res = await fetch(`${baseURL}/settings/languages`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`
    },
    body: JSON.stringify({ languages: form.value.languages })
  })
  showToast(res.ok ? 'Diller güncellendi!' : 'Dil güncelleme hatası!', res.ok ? 'success' : 'error')
}

function handleCertificate(event) {
  const file = event.target.files[0]
  if (file) {
    form.value.certificateFile = file
    form.value.certificateName = file.name
  }
}

async function uploadCertificate() {
  const data = new FormData()
  data.append('file', form.value.certificateFile)

  const res = await fetch(`${baseURL}/settings/certificates`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: data
  })

  if (res.ok) {
    showToast('Sertifika yüklendi.', 'success')
    form.value.certificateFile = null
    form.value.certificateName = ''
  } else {
    showToast('Yüklenemedi.', 'error')
  }
}

function handleAvatarChange(event) {
  const file = event.target.files[0]
  if (file) {
    form.value.avatarFile = file
    previewAvatar.value = URL.createObjectURL(file)
    uploadProfilePhoto()
  }
}

async function uploadProfilePhoto() {
  const data = new FormData()
  data.append('file', form.value.avatarFile)

  const res = await fetch(`${baseURL}/settings/avatar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: data
  })

  if (res.ok) {
    showToast('Profil fotoğrafı yüklendi.', 'success')
    form.value.avatarFile = null
    await fetchProfile() // 👈 burada güncellenmiş URL'yi tekrar çekiyoruz
  } else {
    showToast('Yükleme hatası oluştu.', 'error')
  }
}
</script>

<style scoped>
.profile-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.profile-header {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}
.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ccc;
}
.profile-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.info-group {
  display: flex;
  flex-direction: column;
}
.info-group label {
  font-weight: 600;
  margin-bottom: 5px;
}
.info-group input,
.info-group textarea {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
}
.btn.primary {
  align-self: flex-start;
  padding: 10px 20px;
  background: #0c2d57;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.btn.secondary {
  margin-top: 10px;
  padding: 8px 14px;
  background: #2980b9;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.avatar-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
}
.avatar-wrapper .avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ccc;
}
.edit-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
}
.edit-icon img {
  width: 18px;
  height: 18px;
  filter: invert(1);
}
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 6px;
  color: white;
  font-weight: 500;
  z-index: 9999;
  animation: fadeInOut 3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.toast.success {
  background-color: #2ecc71; /* yeşil */
}

.toast.error {
  background-color: #e74c3c; /* kırmızı */
}
.info-group select {
  margin-top: 5px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background-color: #fff;
  font-size: 14px;
  color: #333;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg fill='gray' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px 16px;
}

.info-group select:focus {
  border-color: #2980b9;
  box-shadow: 0 0 0 2px rgba(41, 128, 185, 0.2);
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, -10px); }
  10%, 90% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -10px); }
}
</style>
